/**
 * Sawyer 11 client-notes-agreements (F1, F4) — the admin client for the four
 * routes this lane adds on /api/admin/crm. Its own file (MERGE-NOTES: every
 * lane's new helpers live in a new lib file, not appended to api.ts).
 *
 * Deploy window: frontends ship on merge, the server later. The deny-by-
 * default guard answers an old server's missing route with 403
 * `route_not_mapped`, so the two READ helpers return a discriminated result
 * instead of throwing: `unavailable` means "nothing to show" and the
 * callers render exactly what the page showed before this lane. Nothing
 * here logs.
 */
import {
  ApiError,
  parseErrorBody,
  readSession,
  type CustomerNote,
} from './api.js';

/** Most customer ids per signals request (the server's 1..200 bound). */
export const CLIENT_NOTE_SIGNALS_CHUNK = 200;

export interface ClientNoteSignal {
  customer_id: string;
  unseen_count: number;
  pinned_total: number;
  /** Newest pins first, at most 3. */
  pinned: CustomerNote[];
  /** Newest first, at most 3. */
  new_notes: CustomerNote[];
}

export type ClientNoteSignalsResult =
  | { kind: 'ok'; signals: ClientNoteSignal[] }
  | { kind: 'unavailable' };

export type AgreementDocumentStatus = 'ok' | 'missing' | 'outdated';

export interface AgreementStandingDocument {
  agreement_id: string;
  display_name: string;
  status: AgreementDocumentStatus;
  required_version: number;
  signed_version: number | null;
  signed_at: string | null;
}

export interface AgreementStanding {
  /** The SAME standing the Front Desk "No signed agreement" chip is built from. */
  standing: 'signed' | 'unsigned' | 'not_required' | 'unknown';
  imported: boolean;
  ever_signed: boolean;
  documents: AgreementStandingDocument[];
}

export type AgreementStandingResult =
  | { kind: 'ok'; standing: AgreementStanding }
  /** Server predates this lane (403 route_not_mapped) or the role may not read it. */
  | { kind: 'unavailable' }
  | { kind: 'error' };

function authHeaders(token: string | null | undefined): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function errorFrom(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const parsed = parseErrorBody(res.status, body, { fallback });
  return new ApiError(res.status, parsed.message, parsed.code);
}

/** Pin or unpin one note. Throws ApiError on failure (the panel reverts). */
export async function setCustomerNotePinned(
  apiUrl: string,
  token: string,
  customerId: string,
  noteId: string,
  pinned: boolean,
): Promise<{ note: CustomerNote }> {
  const res = await fetch(
    `${apiUrl}/api/admin/crm/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}`,
    { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify({ pinned }) },
  );
  if (!res.ok) throw await errorFrom(res, "Couldn't update the pin. Try again.");
  return (await res.json()) as { note: CustomerNote };
}

/**
 * "I have seen this client's notes up to `seenThrough`" (a server-issued
 * created_at). Fire-and-forget: resolves `false` on any failure and never
 * throws or logs — a missed "seen" only means the flag shows again.
 */
export async function markCustomerNotesSeen(
  apiUrl: string,
  token: string,
  customerId: string,
  seenThrough: string,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${apiUrl}/api/admin/crm/customers/${encodeURIComponent(customerId)}/notes/seen`,
      { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ seen_through: seenThrough }) },
    );
    return res.ok;
  } catch {
    return false;
  }
}

function isSignal(x: unknown): x is ClientNoteSignal {
  if (typeof x !== 'object' || x === null) return false;
  const s = x as Record<string, unknown>;
  return (
    typeof s.customer_id === 'string' &&
    typeof s.unseen_count === 'number' &&
    typeof s.pinned_total === 'number' &&
    Array.isArray(s.pinned) &&
    Array.isArray(s.new_notes)
  );
}

/**
 * The desk's batched read, chunked at 200 ids. Any failure — network, 5xx,
 * an old server's 403 — is `unavailable`, never a throw: the desk then shows
 * its rows exactly as before and never claims "no notes".
 */
export async function fetchClientNoteSignals(
  apiUrl: string,
  customerIds: ReadonlyArray<string>,
  token: string | null = readSession().token,
): Promise<ClientNoteSignalsResult> {
  const ids = [...new Set(customerIds.filter((id) => typeof id === 'string' && id.length > 0))];
  if (ids.length === 0) return { kind: 'ok', signals: [] };
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CLIENT_NOTE_SIGNALS_CHUNK) {
    chunks.push(ids.slice(i, i + CLIENT_NOTE_SIGNALS_CHUNK));
  }
  try {
    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const res = await fetch(
          `${apiUrl}/api/admin/crm/notes/signals?customerIds=${chunk.map(encodeURIComponent).join(',')}`,
          { headers: authHeaders(token) },
        );
        if (!res.ok) return null;
        const body = (await res.json()) as { signals?: unknown };
        return Array.isArray(body.signals) ? body.signals.filter(isSignal) : null;
      }),
    );
    if (results.some((r) => r === null)) return { kind: 'unavailable' };
    return { kind: 'ok', signals: results.flat() as ClientNoteSignal[] };
  } catch {
    return { kind: 'unavailable' };
  }
}

/** Per-document standing for the "Signed documents" card. */
export async function fetchAgreementStanding(
  apiUrl: string,
  token: string,
  customerId: string,
): Promise<AgreementStandingResult> {
  try {
    const res = await fetch(
      `${apiUrl}/api/admin/crm/customers/${encodeURIComponent(customerId)}/agreement-standing`,
      { headers: authHeaders(token) },
    );
    if (res.status === 403 || res.status === 404) return { kind: 'unavailable' };
    if (!res.ok) return { kind: 'error' };
    const body = (await res.json()) as Partial<AgreementStanding>;
    if (typeof body.standing !== 'string' || !Array.isArray(body.documents)) return { kind: 'error' };
    return {
      kind: 'ok',
      standing: {
        standing: body.standing,
        imported: body.imported === true,
        ever_signed: body.ever_signed === true,
        documents: body.documents,
      },
    };
  } catch {
    return { kind: 'error' };
  }
}

/**
 * Sawyer #1296 — keep a booking's note on its owner ("Add this to the
 * owner's notes?" → Yes). The server reads the note off the booking itself
 * and adds it at most once; `already_added` is true when an earlier tap (or
 * another device) already kept it. Throws ApiError on failure; the caller
 * shows the error and never undoes the accept / check-in it rode on.
 */
export async function addBookingNoteToOwnerNotes(
  apiUrl: string,
  token: string,
  customerId: string,
  bookingId: string,
): Promise<{ note: CustomerNote; already_added: boolean }> {
  const res = await fetch(
    `${apiUrl}/api/admin/crm/customers/${encodeURIComponent(customerId)}/notes/from-booking`,
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ booking_id: bookingId }) },
  );
  if (!res.ok) throw await errorFrom(res, "Couldn't add it to the owner's notes. Try again.");
  return (await res.json()) as { note: CustomerNote; already_added: boolean };
}
